//@flow
import React, { Fragment } from 'react';
import { defineMessages, FormattedMessage, injectIntl } from 'react-intl';

import formStyles from './styles/FormGeneral.scss';
import stepContactsStyles from './styles/FormStepContacts.scss';

import { WrappedInput } from './fields/WrappedInput';
import { MatchmakerButton } from './fields/MatchmakerButton';
import { MatchmakerInput } from './fields/MatchmakerInput';
import { LastElement } from './fields/LastElement';

import { Budget } from './select/Budget';
import { CountryDropdownRenderer } from './select/CountryDropdownRenderer';

import { BackButton } from './BackButton';

import { TextArea } from 'dibs-elements/exports/TextArea';

const messages = defineMessages({
    additionalInfoPlaceholder: {
        id: 'abt.matchmaker.formReview.additionalInfoPlaceholder',
        defaultMessage: 'Enter additional project details you would like to share.',
    },
});

const placeholder = defineMessages({
    postalCode: {
        id: 'abt.matchmaker.formStepContacts.postalCodePlaceholder',
        defaultMessage: 'Postal code',
    },
    phone: {
        id: 'abt.matchmaker.formStepContacts.phonePlaceholder',
        defaultMessage: 'Phone number',
    },
    email: {
        id: 'abt.matchmaker.formStepContacts.emailPlaceholder',
        defaultMessage: 'Email address',
    },
});

type Props = {|
    intl: intlShape,
    budget: string,
    postalCode: string,
    phone: string,
    country: string,
    email: string,
    tellUsMore: string,
    onChangeBudget: (budget: SyntheticEvent<HTMLInputElement>) => void,
    onChangeCountry: (country: SyntheticEvent<HTMLInputElement>) => void,
    onChangePostalCode: (postalCode: SyntheticEvent<HTMLInputElement>) => void,
    onChangeEmail: (email: SyntheticEvent<HTMLInputElement>) => void,
    onChangePhone: (phone: SyntheticEvent<HTMLInputElement>) => void,
    onChangeMore: (SyntheticEvent<HTMLInputElement>) => void,
    onPageChange: () => void,
    onPageBack: () => void,
    errors: {
        budget?: string,
        postalCode?: string,
        country?: string,
        phone?: string,
        email?: string,
    },
    userId: string,
|};

export const FormStepContactsComponent = (props: Props) => (
    <Fragment>
        <div className={formStyles.contentWrapper}>
            <WrappedInput
                label={
                    <FormattedMessage
                        id="abt.matchmaker.formStepContacts.locationInput"
                        defaultMessage="Location"
                    />
                }
                input={
                    <MatchmakerInput
                        marginRight
                        value={props.postalCode}
                        onChange={props.onChangePostalCode}
                        placeholder={placeholder.postalCode}
                        errorMessage={props.errors.postalCode || ''}
                    />
                }
                secondInput={
                    <CountryDropdownRenderer
                        value={props.country}
                        onChange={props.onChangeCountry}
                        errorMessage={props.errors.country || ''}
                    />
                }
            />
            <WrappedInput
                label={
                    <FormattedMessage
                        id="abt.matchmaker.formStepContacts.budgetInput"
                        defaultMessage="Budget"
                    />
                }
                input={
                    <Budget
                        value={props.budget}
                        onChange={props.onChangeBudget}
                        errorMessage={props.errors.budget || ''}
                    />
                }
            />
            <div className={formStyles.divider} />
            <WrappedInput
                label={
                    <FormattedMessage
                        id="abt.matchmaker.formStepContacts.contactInput"
                        defaultMessage="Your Contact Information"
                    />
                }
                input={
                    !props.userId ? (
                        <MatchmakerInput
                            value={props.email}
                            placeholder={placeholder.email}
                            onChange={props.onChangeEmail}
                            errorMessage={props.errors.email || ''}
                        />
                    ) : (
                        <MatchmakerInput
                            value={props.phone}
                            placeholder={placeholder.phone}
                            onChange={props.onChangePhone}
                            errorMessage={props.errors.phone || ''}
                        />
                    )
                }
                secondInput={
                    !props.userId ? (
                        <MatchmakerInput
                            value={props.phone}
                            placeholder={placeholder.phone}
                            onChange={props.onChangePhone}
                            errorMessage={props.errors.phone || ''}
                        />
                    ) : null
                }
                subText={
                    <div className={stepContactsStyles.privacyText}>
                        {!props.userId ? (
                            <FormattedMessage
                                id="abt.matchmaker.formStepContacts.privacyTextNotLoggedIn"
                                defaultMessage="When receiving your request, our concierge team will schedcule a phone call to share our initial recommendations and learn more about your project."
                            />
                        ) : (
                            <FormattedMessage
                                id="abt.matchmaker.formStepContacts.privacyTextLoggedIn"
                                defaultMessage="We will not share your contact information with any designers."
                            />
                        )}
                    </div>
                }
            />
            <WrappedInput
                label={
                    <FormattedMessage
                        id="abt.matchmaker.formStepContacts.tellUsMore"
                        defaultMessage="Include a message (optional)"
                    />
                }
                input={
                    <TextArea
                        name="tellUsMore"
                        dataTn="matchmaker-input-tellUsMore"
                        onChange={props.onChangeMore}
                        placeholder={props.intl.formatMessage(messages.additionalInfoPlaceholder)}
                        value={props.tellUsMore}
                    />
                }
            />
        </div>
        <div className={stepContactsStyles.contactTextWrapper}>
            <div className={formStyles.divider} />
            <div className={stepContactsStyles.bottomText}>
                <FormattedMessage
                    id="abt.matchmaker.formSteepContacts.bottomText"
                    defaultMessage="A member of our concierge team will reach out within 4 to 5 business days with a curated list of designers that match your needs."
                />
            </div>
        </div>
        <div className={formStyles.contentWrapper}>
            <MatchmakerButton
                style={stepContactsStyles.ctaNext}
                onClick={props.onPageChange}
                buttonName={
                    <FormattedMessage
                        id="abt.matchmaker.formStepContacts.sendInformationButton"
                        defaultMessage="Send Information"
                    />
                }
            />
            <LastElement child={<BackButton onClick={props.onPageBack} />} />
        </div>
    </Fragment>
);

export const FormStepContacts = injectIntl(FormStepContactsComponent);
